import { match } from 'ts-pattern'
import type { ItemType } from '../../../types.ts'
import type {
  Integration,
  IntegrationHealthStatusResponse,
  Repository,
  Tool,
} from '../../../utils/api-client/api-schemas.ts'
import { getIsIntegrationTypeSupportedByApi, type IntegrationType } from '../../../utils/api-client/api-schemas.ts'

export type InstanceHealth = IntegrationHealthStatusResponse

// `health` is absent until the platform has run a connection check on the instance.
export const getInstanceHealth = (integration: Integration): InstanceHealth => integration.health?.status ?? 'unknown'

// Human-readable detail about the last connection check; never contains credential material. Absent
// until a check has run, and absent on a healthy instance — only a failure carries an explanation.
export const getInstanceFailureDetail = (integration: Integration): string | undefined => integration.health?.detail

// `system` instances are owned by the admin console and render read-only; `managed` instances are
// product-managed and therefore editable.
export const getIsSystemInstance = (integration: Integration): boolean => integration.source === 'system'

// Every instance carries a required, user-facing name, so it alone identifies an instance in prose.
export const getInstanceName = (integration: Integration): string => integration.name

// The product catalog of tools Pixee can integrate with, in the order design lists them.
// `icon` selects the brand glyph (an SCM type → SCMIcon, a Tool → ToolIcon, or none for tools
// without an asset yet). `addableType` carries the Integration `type` the "+" should open the Setup
// drawer for; an entry is addable exactly when it names a creatable type.
export type CatalogIcon = { kind: 'scm'; type: Repository['type'] } | { kind: 'tool'; tool: Tool } | { kind: 'none' }

// Every tool whose setup form, fields and docs are built. This says nothing about what may be sent:
// the spec's request union decides that, and the catalog gates on both.
const CREATABLE_INTEGRATION_TYPES = [
  'datadog',
  'arnica',
  'sonar',
  'veracode',
  'checkmarx',
  'polaris',
  'appscan',
] as const satisfies readonly IntegrationType[]

export type CreatableIntegrationType = ItemType<typeof CREATABLE_INTEGRATION_TYPES>

const CREATABLE_INTEGRATION_TYPE_SET = new Set<string>(CREATABLE_INTEGRATION_TYPES)

/**
 * Whether a setup form, fields and docs exist for this type in the product. Distinct from
 * `getIsIntegrationTypeSupportedByApi`, which answers whether the *spec* has a write contract for
 * it — a type can have one without the other, which is why the catalog gates on both.
 */
export const getHasIntegrationSetupForm = (type: string): type is CreatableIntegrationType =>
  CREATABLE_INTEGRATION_TYPE_SET.has(type)

export type CatalogEntry = {
  id: string
  label: string
  icon: CatalogIcon
  isAddable: boolean
  addableType?: CreatableIntegrationType
}

/**
 * Exported so stories and tests select a real entry instead of hand-writing one. Hand-written
 * fixtures had already drifted into `isAddable: true` with no `addableType`, a combination
 * `withAddabilityGated` cannot produce.
 */
export const getCatalogEntry = (id: string): CatalogEntry | undefined => TOOL_CATALOG.find(entry => entry.id === id)

const TOOL_CATALOG: CatalogEntry[] = [
  { id: 'azure', label: 'Azure DevOps', icon: { kind: 'scm', type: 'azure' }, isAddable: false },
  {
    id: 'appscan',
    label: 'HCL AppScan',
    icon: { kind: 'tool', tool: 'appscan' },
    isAddable: true,
    addableType: 'appscan',
  },
  {
    id: 'arnica',
    label: 'Arnica',
    icon: { kind: 'tool', tool: 'arnica_sast' },
    isAddable: true,
    addableType: 'arnica',
  },
  { id: 'bitbucket', label: 'Bitbucket', icon: { kind: 'scm', type: 'bitbucket' }, isAddable: false },
  {
    id: 'blackduck',
    label: 'Black Duck',
    icon: { kind: 'tool', tool: 'polaris_sast' },
    isAddable: true,
    addableType: 'polaris',
  },
  {
    id: 'checkmarx',
    label: 'Checkmarx',
    icon: { kind: 'tool', tool: 'checkmarx' },
    isAddable: true,
    addableType: 'checkmarx',
  },
  {
    id: 'datadog',
    label: 'Datadog',
    icon: { kind: 'tool', tool: 'datadog_sast' },
    isAddable: true,
    addableType: 'datadog',
  },
  { id: 'github', label: 'GitHub', icon: { kind: 'scm', type: 'github' }, isAddable: false },
  { id: 'gitlab', label: 'GitLab', icon: { kind: 'scm', type: 'gitlab' }, isAddable: false },
  { id: 'sonar', label: 'SonarQube', icon: { kind: 'tool', tool: 'sonar' }, isAddable: true, addableType: 'sonar' },
  {
    id: 'veracode',
    label: 'Veracode',
    icon: { kind: 'tool', tool: 'veracode' },
    isAddable: true,
    addableType: 'veracode',
  },
]

// Source providers (SCM) supply repositories and open PRs and are connected at install time; only the
// tools that supply findings are listed in the Available catalog. An entry whose type has no write
// contract in the spec stays listed but loses its Add action.
const withAddabilityGated = (entry: CatalogEntry): CatalogEntry =>
  entry.isAddable && entry.addableType && !getIsIntegrationTypeSupportedByApi(entry.addableType)
    ? { ...entry, isAddable: false }
    : entry

export const TOOL_INTEGRATION_CATALOG: CatalogEntry[] = TOOL_CATALOG.filter(entry => entry.icon.kind !== 'scm').map(
  withAddabilityGated
)

/**
 * The name an instance goes by once it is configured — deliberately not always the catalog's label.
 * The catalog sells the product ("Black Duck", "HCL AppScan"); a configured instance names the tool
 * it actually is ("Black Duck Polaris", "AppScan"). Confirmed with design rather than drift, so a
 * future reader does not "unify" these into one table.
 */
export const getProviderLabel = (type: Integration['type']): string =>
  match(type)
    .with('sonar', () => 'SonarQube')
    .with('checkmarx', () => 'Checkmarx')
    .with('veracode', () => 'Veracode')
    .with('appscan', () => 'AppScan')
    .with('arnica', () => 'Arnica')
    .with('datadog', () => 'Datadog')
    .with('polaris', () => 'Black Duck Polaris')
    .with('github', () => 'GitHub')
    .with('gitlab', () => 'GitLab')
    .with('azure', () => 'Azure DevOps')
    .with('bitbucket', () => 'Bitbucket')
    .exhaustive()

// The Checkmarx regions the platform resolves API/auth base URLs from. Kept local: Checkmarx has no
// Create/Update variant in the spec yet, so there is no `CheckmarxRegion` schema to mirror.
export const CHECKMARX_REGIONS = ['US', 'US2', 'EU', 'EU2', 'DEU', 'ANZ', 'IND', 'SNG', 'MEA'] as const

export type CheckmarxRegion = (typeof CHECKMARX_REGIONS)[number]

export const DEFAULT_CHECKMARX_REGION: CheckmarxRegion = CHECKMARX_REGIONS[0]

// A single connection/credential field of a creatable integration. `key` is the snake_case request
// body key; `secret` marks a write-only credential (rendered empty on the Manage drawer and only
// submitted when re-entered to rotate). `select` fields carry `options`; `text`/`password` fields
// may carry a `defaultValue` prefilled on Setup.
export type IntegrationFieldKind = 'text' | 'password' | 'select'

export type IntegrationField = {
  key: string
  label: string
  kind: IntegrationFieldKind
  secret?: boolean
  placeholder?: string
  helperText?: string
  defaultValue?: string
  options?: { value: string; label: string }[]
}

const DATADOG_DEFAULT_BASE_URI = 'https://api.datadoghq.com'

const BASE_URI_FIELD = (helperText?: string): IntegrationField => ({
  key: 'base_uri',
  label: 'API base URI',
  kind: 'text',
  helperText,
})

// The per-tool connection/credential fields, keyed by Integration type. The Name field is rendered
// by the drawers themselves (always optional, never a credential) and so is intentionally absent
// here. AppScan's webhook-auth fields are also absent: the drawer renders them through a method
// chooser (secret vs. basic auth) because exactly one method may be submitted.
export const TOOL_FIELD_CONFIG: Record<CreatableIntegrationType, IntegrationField[]> = {
  datadog: [
    {
      ...BASE_URI_FIELD('EU and other regions use a different host (e.g. https://api.datadoghq.eu).'),
      defaultValue: DATADOG_DEFAULT_BASE_URI,
    },
    { key: 'api_key', label: 'API key', kind: 'password', secret: true },
    { key: 'application_key', label: 'Application key', kind: 'password', secret: true },
  ],
  arnica: [BASE_URI_FIELD(), { key: 'api_key', label: 'API key', kind: 'password', secret: true }],
  sonar: [BASE_URI_FIELD(), { key: 'token', label: 'Token', kind: 'password', secret: true }],
  veracode: [
    BASE_URI_FIELD(),
    { key: 'api_key_id', label: 'API key ID', kind: 'password', secret: true },
    { key: 'api_key_secret', label: 'API key secret', kind: 'password', secret: true },
  ],
  checkmarx: [
    {
      key: 'region',
      label: 'Region',
      kind: 'select',
      helperText: 'The region resolves the API and auth base URLs.',
      options: CHECKMARX_REGIONS.map(region => ({ value: region, label: region })),
    },
    { key: 'tenant_account_name', label: 'Tenant account name', kind: 'text' },
    { key: 'api_key', label: 'API key', kind: 'password', secret: true, helperText: 'The OAuth refresh token.' },
  ],
  polaris: [BASE_URI_FIELD(), { key: 'token', label: 'Token', kind: 'password', secret: true }],
  appscan: [
    BASE_URI_FIELD(),
    { key: 'api_key_id', label: 'API key ID', kind: 'password', secret: true },
    { key: 'api_key_secret', label: 'API key secret', kind: 'password', secret: true },
  ],
}

// Credential-connection guidance shown in a tool's Documentation drawer: a one-paragraph summary of
// the connection model and the credentials needed, followed by ordered steps for obtaining them in
// the vendor's product, and a link to the vendor's own docs. Vendor menu paths are described loosely
// ("for example", "open …") because exact labels drift between vendor releases.
export type ToolDoc = {
  summary: string
  steps: string[]
  docsUrl?: string
  docsLabel?: string
}

export const TOOL_DOCS: Record<CreatableIntegrationType, ToolDoc> = {
  datadog: {
    summary:
      'Pixee connects to the Datadog API and pulls Code Security (SAST) findings for your repositories. You will need an API key and an Application key from your Datadog organization.',
    steps: [
      'In Datadog, open Organization Settings → API Keys and create or copy an API key.',
      'Open Organization Settings → Application Keys and create an Application key with access to Code Security.',
      'Identify your regional API host — US1 is https://api.datadoghq.com, EU is https://api.datadoghq.eu.',
      'In the Setup drawer, enter the API base URI, then paste the API key and Application key.',
    ],
    docsUrl: 'https://docs.datadoghq.com/account_management/api-app-keys/',
    docsLabel: 'Datadog API & application keys',
  },
  arnica: {
    summary:
      'Pixee connects to your Arnica tenant and pulls SAST findings. You will need your Arnica API base URI and an API key.',
    steps: [
      'In Arnica, open your organization settings and generate an API key.',
      'Copy your Arnica API base URI.',
      'In the Setup drawer, enter the API base URI and paste the API key.',
    ],
    docsUrl: 'https://docs.arnica.io/',
    docsLabel: 'Arnica documentation',
  },
  sonar: {
    summary:
      'Pixee connects to SonarQube or SonarCloud and pulls issues and security hotspots. You will need your Sonar base URI and an access token.',
    steps: [
      'In SonarQube or SonarCloud, go to My Account → Security.',
      'Generate a token — a User token, or a project analysis token scoped to the projects Pixee should read.',
      'Copy your Sonar base URI. SonarCloud is https://sonarcloud.io; for SonarQube use your server URL.',
      'In the Setup drawer, enter the API base URI and paste the token.',
    ],
    docsUrl: 'https://docs.sonarsource.com/sonarqube-server/latest/user-guide/managing-tokens/',
    docsLabel: 'SonarQube tokens',
  },
  veracode: {
    summary:
      'Pixee connects to the Veracode API with HMAC credentials and pulls findings. You will need an API key ID and an API key secret.',
    steps: [
      'In the Veracode Platform, open your account menu → API Credentials.',
      'Generate API credentials and copy the API ID and the API key — the secret is shown only once.',
      'Identify your regional API host — https://api.veracode.com (US) or https://api.veracode.eu (EU).',
      'In the Setup drawer, enter the API base URI, then paste the API key ID and API key secret.',
    ],
    docsUrl: 'https://docs.veracode.com/r/c_api_credentials',
    docsLabel: 'Veracode API credentials',
  },
  checkmarx: {
    summary:
      'Pixee connects to Checkmarx One using an OAuth refresh token and pulls scan results. You will need your region, tenant account name, and an API key.',
    steps: [
      'In Checkmarx One, open your user menu → Create API Key (or Settings → Identity and Access Management → API Keys).',
      'Generate an API key — this is an OAuth refresh token. Copy it; it is shown only once.',
      'Note your tenant (account) name and the region your tenant runs in.',
      'In the Setup drawer, choose the region, enter the tenant account name, and paste the API key.',
    ],
    docsUrl: 'https://docs.checkmarx.com/en/34965-68618-generating-an-api-key.html',
    docsLabel: 'Checkmarx One API keys',
  },
  polaris: {
    summary:
      'Pixee connects to Black Duck Polaris and pulls findings. You will need your Polaris base URI and an access token.',
    steps: [
      'In Polaris, open My Profile → Access Tokens.',
      'Create an access token and copy it.',
      'Copy your Polaris base URI, for example https://polaris.blackduck.com.',
      'In the Setup drawer, enter the API base URI and paste the token.',
    ],
    docsUrl: 'https://documentation.blackduck.com/',
    docsLabel: 'Black Duck Polaris documentation',
  },
  appscan: {
    summary:
      'HCL AppScan pushes scan results to Pixee through a webhook, and Pixee calls back into AppScan to create fixes. You will need AppScan API credentials plus a webhook authentication method — either a shared secret or a basic-auth username and password.',
    steps: [
      'In HCL AppScan on Cloud, open Settings → API Key Management and generate a Key ID and Secret.',
      'Copy your AppScan base URI, for example https://cloud.appscan.com.',
      'Choose a webhook authentication method: a shared secret, or a basic-auth username and password.',
      'In the Setup drawer, enter the API base URI, the API key ID and secret, and your chosen webhook authentication.',
      'Configure AppScan to send its webhook to the Pixee URL shown after setup, using the same authentication.',
    ],
    docsUrl: 'https://help.hcl-software.com/appscan/ASoC/',
    docsLabel: 'HCL AppScan on Cloud documentation',
  },
}

// Source providers (SCM) are connected at install time and managed by an administrator, so their
// Documentation drawer explains where they are managed rather than how to add credentials here.
const SCM_DOC: ToolDoc = {
  summary:
    'Source providers supply repositories and open pull requests. They are connected when Pixee is installed and managed by your administrator — there is nothing to add here.',
  steps: [],
}

export const getCatalogEntryDoc = (entry: CatalogEntry): ToolDoc =>
  entry.addableType ? TOOL_DOCS[entry.addableType] : SCM_DOC

// Resolves the catalog entry for an integration's type, so a drawer holding a configured instance can
// open that tool's documentation.
export const getCatalogEntryForType = (type: Integration['type']): CatalogEntry | undefined =>
  TOOL_CATALOG.find(entry => entry.addableType === type)
